import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import type { DefaultHomeTab } from "@/constants/AppNavigation";
import Feather from "@expo/vector-icons/Feather";
import firestore from "@react-native-firebase/firestore";
import { router } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Pressable, StyleSheet } from "react-native";

const SettingsDefaultHomePage = () => {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);

  const selected: DefaultHomeTab =
    userData?.defaultHomeTab === "groups" ? "groups" : "feed";

  const handleSelect = async (value: DefaultHomeTab) => {
    if (!user || saving || value === selected) return;
    setSaving(true);
    try {
      await firestore()
        .collection("users")
        .doc(user.uid)
        .set({ defaultHomeTab: value }, { merge: true });
      addToast(t("SETTINGS_SAVED"), "success");
    } catch (error) {
      console.error("Error saving default home setting:", error);
      Alert.alert(t("ERROR"), t("NETWORK_ERROR"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CustomHeader
        title={t("DEFAULT_HOME")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={() => router.replace("/(tabs)/settings")}
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.description}>
          {t("DEFAULT_HOME_DESCRIPTION")}
        </ThemedText>

        <OptionRow
          title={t("DEFAULT_HOME_FEED")}
          description={t("DEFAULT_HOME_FEED_DESCRIPTION")}
          selected={selected === "feed"}
          onPress={() => handleSelect("feed")}
        />
        <OptionRow
          title={t("DEFAULT_HOME_GROUPS")}
          description={t("DEFAULT_HOME_GROUPS_DESCRIPTION")}
          selected={selected === "groups"}
          onPress={() => handleSelect("groups")}
        />
      </ThemedView>
    </>
  );
};

const OptionRow = ({
  title,
  description,
  selected,
  onPress,
}: {
  title: string;
  description: string;
  selected: boolean;
  onPress: () => void;
}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="radio"
    accessibilityState={{ selected }}
    style={styles.optionRow}
  >
    <ThemedView style={styles.optionLabel}>
      <ThemedText style={styles.optionTitle}>{title}</ThemedText>
      <ThemedText style={styles.optionDescription}>{description}</ThemedText>
    </ThemedView>
    {selected && <Feather name="check" size={22} color="#0a7ea4" />}
  </Pressable>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  description: {
    fontSize: 13,
    opacity: 0.75,
    marginBottom: 16,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  optionLabel: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  optionDescription: {
    fontSize: 13,
    marginTop: 4,
    opacity: 0.7,
  },
});

export default SettingsDefaultHomePage;
